import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  getMyFieldMaps,
  getPublicFieldMaps,
  createFieldMap,
  updateFieldMap,
  deleteFieldMap,
  getFieldNodes,
  getNodeByNodeCode,
  assignNodeToMap,
  unassignNodeFromMap,
  getFieldOfficeNode,
  assignOfficeNode,
  updateOfficeNode,
  removeOfficeNode,
  FieldMap,
  FieldMapPayload,
  FieldNode,
  FieldOfficeNode,
} from '../../services/fieldService';
import {
  getMainRefereeMaps,
  createMainRefereeMap,
  updateMainRefereeMap,
  deleteMainRefereeMap,
  getMainRefereeNodes,
  getMainRefereeOfficeNode,
} from '../../services/mainRefereeService';
import FieldInfoButton from './FieldInfoButton';
import ConfirmModal from '../common/ConfirmModal';
import { useConfirmModal } from '../../hooks/useConfirmModal';
import { fieldMapsInfo } from './fieldMapsInfo';
import { mainRefereeMapsInfo } from './mainRefereeInfo';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Title = styled.h3`
  color: #00d4ff;
  margin: 0;
  font-size: 1.2rem;
  font-weight: bold;
`;

const ActionButton = styled.button`
  background: #00d4ff;
  border: 1px solid #00d4ff;
  color: #00121f;
  padding: 10px 20px;
  border-radius: 10px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;

  &:hover {
    background: #00e0ff;
    box-shadow: 0 0 15px rgba(0, 212, 255, 0.45);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  border: 1px solid rgba(0, 212, 255, 0.5);
  color: #00d4ff;
  padding: 8px 16px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.85rem;

  &:hover {
    background: rgba(0, 212, 255, 0.1);
    border-color: #00d4ff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DangerButton = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 68, 68, 0.5);
  color: #ff4444;
  padding: 8px 16px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.85rem;

  &:hover {
    background: rgba(255, 68, 68, 0.1);
    border-color: #ff4444;
  }
`;

const MapGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const MapCard = styled.div`
  background: #101d2c;
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    border-color: rgba(0, 212, 255, 0.5);
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
  }
`;

const MapImage = styled.div<{ $imageUrl?: string }>`
  width: 100%;
  height: 150px;
  background: ${props => props.$imageUrl ? `url(${props.$imageUrl})` : 'linear-gradient(135deg, #1a2a3a 0%, #0d1a26 100%)'};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 0.9rem;
`;

const MapContent = styled.div`
  padding: 16px;
`;

const MapName = styled.h4`
  color: #00d4ff;
  margin: 0 0 8px 0;
  font-size: 1.1rem;
`;

const MapDescription = styled.p`
  color: #b0b0b0;
  margin: 0 0 12px 0;
  font-size: 0.85rem;
  line-height: 1.4;
`;

const MapMeta = styled.div`
  display: flex;
  gap: 12px;
  font-size: 0.8rem;
  color: #888;
  margin-bottom: 12px;
`;

const MapActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
  font-size: 1rem;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;

  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 3px solid rgba(0, 212, 255, 0.3);
    border-radius: 50%;
    border-top-color: #00d4ff;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: rgba(255, 68, 68, 0.1);
  border: 1px solid rgba(255, 68, 68, 0.3);
  color: #ff6b6b;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 0.9rem;
`;

const SuccessMessage = styled.div`
  background: rgba(0, 255, 136, 0.1);
  border: 1px solid rgba(0, 255, 136, 0.3);
  color: #00ff88;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 0.9rem;
`;

// Modal styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: #0d1a26;
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h3`
  color: #00d4ff;
  margin: 0;
  font-size: 1.3rem;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #888;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;

  &:hover {
    color: #fff;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  display: block;
  color: #FFFFFF;
  font-size: 0.9rem;
  margin-bottom: 8px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px;
  background: #1a2a3a;
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  color: #fff;
  font-size: 0.95rem;

  &:focus {
    outline: none;
    border-color: #00d4ff;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px;
  background: #1a2a3a;
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  color: #fff;
  font-size: 0.95rem;
  resize: vertical;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: #00d4ff;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

const FileInputWrapper = styled.div`
  position: relative;
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputLabel = styled.label`
  display: inline-block;
  padding: 12px 20px;
  background: rgba(0, 212, 255, 0.1);
  border: 1px dashed rgba(0, 212, 255, 0.5);
  border-radius: 8px;
  color: #00d4ff;
  cursor: pointer;
  text-align: center;
  width: 100%;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 212, 255, 0.2);
    border-style: solid;
  }
`;

const ImagePreview = styled.div`
  margin-top: 12px;
  border-radius: 8px;
  overflow: hidden;
  max-width: 200px;

  img {
    width: 100%;
    display: block;
  }
`;

// Nodes section styles
const NodesSection = styled.div`
  margin-top: 20px;
  padding: 16px;
  background: rgba(0, 30, 60, 0.5);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 10px;
`;

const SectionTitle = styled.div`
  font-size: 1rem;
  color: #00d4ff;
  margin-bottom: 16px;
  font-weight: 600;
`;

const NodesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const NodeItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: rgba(0, 20, 40, 0.6);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 6px;
`;

const NodeInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const NodeName = styled.span`
  font-size: 0.9rem;
  color: #fff;
`;

const NodeMeta = styled.span`
  font-size: 0.85rem;
  color: #FFFFFF;
  font-weight: 700;
`;

const NodeTypeBadge = styled.span<{ $type: string }>`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.7rem;
  text-transform: uppercase;
  margin-left: 8px;
  background: ${({ $type }) => {
    switch ($type) {
      case 'InternetNode': return 'rgba(255, 165, 0, 0.2)';
      case 'MapMainNode': return 'rgba(0, 212, 255, 0.2)';
      case 'MapNode': return 'rgba(0, 255, 136, 0.2)';
      default: return 'rgba(128, 128, 128, 0.2)';
    }
  }};
  color: ${({ $type }) => {
    switch ($type) {
      case 'InternetNode': return '#ffa500';
      case 'MapMainNode': return '#00d4ff';
      case 'MapNode': return '#00ff88';
      default: return '#888';
    }
  }};
`;

const AddNodeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
`;

const SaleNumberInput = styled.input`
  flex: 1;
  padding: 10px 12px;
  background: #1a2a3a;
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  color: #fff;
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: #00d4ff;
  }

  &::placeholder {
    color: #666;
  }
`;

const FoundNodeCard = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SearchingText = styled.span`
  color: #888;
  font-size: 0.85rem;
  margin-top: 8px;
  display: block;
`;

// Main Node Section Styles
const MainNodeSection = styled.div`
  background: linear-gradient(135deg, rgba(255, 165, 0, 0.1) 0%, rgba(0, 30, 60, 0.5) 100%);
  border: 1px solid rgba(255, 165, 0, 0.3);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
`;

const MainNodeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const MainNodeTitle = styled.h4`
  color: #ffa500;
  margin: 0;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MainNodeIcon = styled.span`
  font-size: 1.2rem;
`;

const MainNodeCard = styled.div`
  background: rgba(0, 20, 40, 0.6);
  border: 1px solid rgba(255, 165, 0, 0.2);
  border-radius: 8px;
  padding: 16px;
`;

const MainNodeInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MainNodeName = styled.div`
  font-size: 1rem;
  color: #fff;
  font-weight: 500;
`;

const MainNodeDescription = styled.div`
  font-size: 0.85rem;
  color: #b0b0b0;
  line-height: 1.4;
`;

const MainNodeMeta = styled.div`
  font-size: 0.8rem;
  color: #888;
  margin-top: 4px;
`;

const MainNodeActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const MainNodeEmpty = styled.div`
  text-align: center;
  padding: 20px;
  color: #888;
  font-size: 0.9rem;
`;

interface FieldMapsSectionProps {
  fieldId: string;
  useMainRefereeApi?: boolean; // Если true - используем API main referee с refereeToken
  isOwner?: boolean; // Если true - показываем кнопки редактирования, если false - только просмотр
}

interface FormState {
  name: string;
  description: string;
  schemaVideoUrl: string;
  youtubeVideos: string[];
}

const initialFormState: FormState = {
  name: '',
  description: '',
  schemaVideoUrl: '',
  youtubeVideos: [],
};

const FieldMapsSection: React.FC<FieldMapsSectionProps> = ({ fieldId, useMainRefereeApi = false, isOwner = true }) => {
  const navigate = useNavigate();
  const { confirm, modalProps: confirmModalProps } = useConfirmModal();
  const [maps, setMaps] = useState<FieldMap[]>([]);
  const [nodes, setNodes] = useState<FieldNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [nodesModalOpen, setNodesModalOpen] = useState(false);
  const [editingMap, setEditingMap] = useState<FieldMap | null>(null);
  const [selectedMap, setSelectedMap] = useState<FieldMap | null>(null);
  const [formState, setFormState] = useState<FormState>(initialFormState);
  // Schema image
  const [schemaImageFile, setSchemaImageFile] = useState<File | null>(null);
  const [schemaImagePreview, setSchemaImagePreview] = useState<string | null>(null);
  // Map images (up to 5)
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [imagesToDelete, setImagesToDelete] = useState<string[]>([]);
  // YouTube video input
  const [youtubeInput, setYoutubeInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [nodeCodeInput, setNodeCodeInput] = useState('');
  const [positionInput, setPositionInput] = useState('');
  const [foundNode, setFoundNode] = useState<FieldNode | null>(null);
  const [searchingNode, setSearchingNode] = useState(false);
  const [nodeSearchError, setNodeSearchError] = useState<string | null>(null);
  const schemaImageInputRef = useRef<HTMLInputElement>(null);
  const imagesInputRef = useRef<HTMLInputElement>(null);

  // Main Node states
  const [officeNode, setOfficeNode] = useState<FieldOfficeNode | null>(null);
  const [mainNodeModalOpen, setMainNodeModalOpen] = useState(false);
  const [mainNodeCode, setMainNodeCode] = useState('');
  const [mainNodeName, setMainNodeName] = useState('');
  const [mainNodeDescription, setMainNodeDescription] = useState('');
  const [mainNodeSearching, setMainNodeSearching] = useState(false);
  const [mainNodeError, setMainNodeError] = useState<string | null>(null);
  const [foundMainNode, setFoundMainNode] = useState<FieldNode | null>(null);
  const [editingMainNode, setEditingMainNode] = useState(false);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (useMainRefereeApi) {
        // Main referee API - загружаем maps, nodes и office node через referee endpoints
        const [mapsData, nodesData, officeNodeData] = await Promise.all([
          getMainRefereeMaps(),
          getMainRefereeNodes(),
          getMainRefereeOfficeNode(),
        ]);
        // Преобразуем FieldMapResponse в FieldMap (добавляем недостающие поля)
        const maps: FieldMap[] = mapsData.map(m => ({
          ...m,
          isActive: true,
          devices: [],
          deviceCount: 0,
          fieldNodesIds: [],
          fieldNodes: [],
          createdAt: m.id, // заглушка
          updatedAt: m.id, // заглушка
        }));
        setMaps(maps);
        // Преобразуем FieldNodeResponse в FieldNode
        const fieldNodes: FieldNode[] = nodesData.map(n => ({
          ...n,
        }));
        setNodes(fieldNodes);
        // Преобразуем FieldOfficeNodeResponse в FieldOfficeNode
        const officeNode: FieldOfficeNode | null = officeNodeData ? {
          ...officeNodeData,
        } : null;
        setOfficeNode(officeNode);
      } else if (isOwner) {
        // Owner - загружаем через авторизованный API
        const [mapsData, nodesData, officeNodeData] = await Promise.all([
          getMyFieldMaps(),
          getFieldNodes(fieldId),
          getFieldOfficeNode(),
        ]);
        setMaps(mapsData);
        setNodes(nodesData);
        setOfficeNode(officeNodeData);
      } else {
        // Public view - только карты без nodes и office node
        const mapsData = await getPublicFieldMaps(fieldId);
        setMaps(mapsData);
        setNodes([]);
        setOfficeNode(null);
      }
    } catch (err) {
      console.error('Failed to load data', err);
      setError(err instanceof Error ? err.message : 'Failed to load maps');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fieldId) {
      loadData();
    }
  }, [fieldId]);

  const handleCreateClick = () => {
    setEditingMap(null);
    setFormState(initialFormState);
    setSchemaImageFile(null);
    setSchemaImagePreview(null);
    setImageFiles([]);
    setImagePreviews([]);
    setExistingImages([]);
    setImagesToDelete([]);
    setYoutubeInput('');
    setError(null);
    setSuccess(null);
    setModalOpen(true);
  };

  const handleEditClick = (map: FieldMap) => {
    setEditingMap(map);
    setFormState({
      name: map.name,
      description: map.description,
      schemaVideoUrl: map.schemaVideoUrl || '',
      youtubeVideos: map.youtubeVideos || [],
    });
    setSchemaImageFile(null);
    setSchemaImagePreview(map.schemaImageUrl || null);
    setImageFiles([]);
    setImagePreviews([]);
    setExistingImages(map.images || []);
    setImagesToDelete([]);
    setYoutubeInput('');
    setError(null);
    setSuccess(null);
    setModalOpen(true);
  };

  const handleNodesClick = (map: FieldMap) => {
    setSelectedMap(map);
    setNodeCodeInput('');
    setPositionInput('');
    setFoundNode(null);
    setNodeSearchError(null);
    setError(null);
    setSuccess(null);
    setNodesModalOpen(true);
  };

  const handleDeleteClick = async (map: FieldMap) => {
    const confirmed = await confirm({
      title: 'Delete Map',
      message: `Are you sure you want to delete "${map.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      if (useMainRefereeApi) {
        await deleteMainRefereeMap(map.id);
      } else {
        await deleteFieldMap(map.id);
      }
      setSuccess('Map deleted');
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete map');
    }
  };

  const handleSchemaImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSchemaImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setSchemaImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const totalImages = existingImages.length - imagesToDelete.length + imageFiles.length + files.length;
      const availableSlots = 5 - (existingImages.length - imagesToDelete.length + imageFiles.length);
      const filesToAdd = Array.from(files).slice(0, availableSlots);

      if (filesToAdd.length < files.length) {
        setError(`Can only add ${availableSlots} more images (max 5 total)`);
      }

      const newFiles = [...imageFiles, ...filesToAdd];
      setImageFiles(newFiles);

      // Create previews for new files
      filesToAdd.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreviews(prev => [...prev, reader.result as string]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleRemoveNewImage = (index: number) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = (imageUrl: string) => {
    setImagesToDelete(prev => [...prev, imageUrl]);
  };

  const handleAddYoutubeVideo = () => {
    if (youtubeInput.trim() && formState.youtubeVideos.length < 10) {
      setFormState(prev => ({
        ...prev,
        youtubeVideos: [...prev.youtubeVideos, youtubeInput.trim()],
      }));
      setYoutubeInput('');
    }
  };

  const handleRemoveYoutubeVideo = (index: number) => {
    setFormState(prev => ({
      ...prev,
      youtubeVideos: prev.youtubeVideos.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!formState.name.trim()) {
      setError('Map name is required');
      return;
    }

    const payload: FieldMapPayload = {
      name: formState.name.trim(),
      fieldId,
      description: formState.description.trim(),
      schemaVideoUrl: formState.schemaVideoUrl.trim() || undefined,
      youtubeVideos: formState.youtubeVideos,
      devices: [],
    };

    setSaving(true);
    try {
      if (editingMap) {
        if (useMainRefereeApi) {
          await updateMainRefereeMap(
            editingMap.id,
            payload,
            schemaImageFile || undefined,
            imageFiles.length > 0 ? imageFiles : undefined,
            imagesToDelete.length > 0 ? imagesToDelete : undefined
          );
        } else {
          await updateFieldMap(
            editingMap.id,
            payload,
            schemaImageFile || undefined,
            imageFiles.length > 0 ? imageFiles : undefined,
            imagesToDelete.length > 0 ? imagesToDelete : undefined
          );
        }
        setSuccess('Map updated');
      } else {
        if (useMainRefereeApi) {
          await createMainRefereeMap(
            payload,
            schemaImageFile || undefined,
            imageFiles.length > 0 ? imageFiles : undefined
          );
        } else {
          await createFieldMap(
            payload,
            schemaImageFile || undefined,
            imageFiles.length > 0 ? imageFiles : undefined
          );
        }
        setSuccess('Map created');
      }
      setModalOpen(false);
      setFormState(initialFormState);
      setEditingMap(null);
      setSchemaImageFile(null);
      setSchemaImagePreview(null);
      setImageFiles([]);
      setImagePreviews([]);
      setExistingImages([]);
      setImagesToDelete([]);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save map');
    } finally {
      setSaving(false);
    }
  };

  const handleSearchNode = async () => {
    if (!nodeCodeInput.trim()) return;

    setSearchingNode(true);
    setNodeSearchError(null);
    setFoundNode(null);

    try {
      const node = await getNodeByNodeCode(nodeCodeInput.trim());

      // Проверяем, не привязан ли уже к этой карте
      if (node.mapId === selectedMap?.id) {
        setNodeSearchError('This node is already assigned to this map');
        return;
      }

      setFoundNode(node);
    } catch (err) {
      setNodeSearchError(err instanceof Error ? err.message : 'Node not found');
    } finally {
      setSearchingNode(false);
    }
  };

  const handleAssignFoundNode = async () => {
    if (!foundNode || !selectedMap) return;

    try {
      await assignNodeToMap(foundNode.id, selectedMap.id, positionInput.trim() || undefined);
      setSuccess('Node assigned to map');
      setNodeCodeInput('');
      setPositionInput('');
      setFoundNode(null);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign node');
    }
  };

  const handleUnassignNode = async (nodeId: string) => {
    try {
      await unassignNodeFromMap(nodeId);
      setSuccess('Node removed from map');
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove node');
    }
  };

  // Main Node handlers
  const handleSetMainNodeClick = () => {
    setMainNodeCode('');
    setMainNodeName('');
    setMainNodeDescription('');
    setFoundMainNode(null);
    setMainNodeError(null);
    setEditingMainNode(false);
    setMainNodeModalOpen(true);
  };

  const handleEditMainNodeClick = () => {
    if (officeNode) {
      setMainNodeName(officeNode.name || '');
      setMainNodeDescription(officeNode.description || '');
      setEditingMainNode(true);
      setMainNodeModalOpen(true);
    }
  };

  const handleSearchMainNode = async () => {
    if (!(mainNodeCode || '').trim()) return;

    setMainNodeSearching(true);
    setMainNodeError(null);
    setFoundMainNode(null);

    try {
      const node = await getNodeByNodeCode((mainNodeCode || '').trim());

      // Check if it's OfficeNode
      if (node.nodeType !== 'OfficeNode') {
        setMainNodeError('Only OfficeNode can be set as Main Node. This is a ' + node.nodeType);
        return;
      }

      // Check if already assigned to another field
      if (node.fieldId && node.fieldId !== fieldId) {
        setMainNodeError('This node is already assigned to another field');
        return;
      }

      setFoundMainNode(node);
      setMainNodeName(node.friendlyName);
    } catch (err) {
      setMainNodeError(err instanceof Error ? err.message : 'Node not found');
    } finally {
      setMainNodeSearching(false);
    }
  };

  const handleAssignMainNode = async () => {
    if (!foundMainNode || !(mainNodeName || '').trim()) return;

    setSaving(true);
    setMainNodeError(null);
    try {
      await assignOfficeNode(foundMainNode.nodeCode, (mainNodeName || '').trim(), (mainNodeDescription || '').trim() || undefined);
      setSuccess('Main Node assigned successfully');
      setMainNodeModalOpen(false);
      await loadData();
    } catch (err) {
      setMainNodeError(err instanceof Error ? err.message : 'Failed to assign main node');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateMainNode = async () => {
    if (!(mainNodeName || '').trim()) return;

    setSaving(true);
    setMainNodeError(null);
    try {
      await updateOfficeNode((mainNodeName || '').trim(), (mainNodeDescription || '').trim() || undefined);
      setSuccess('Main Node updated successfully');
      setMainNodeModalOpen(false);
      await loadData();
    } catch (err) {
      setMainNodeError(err instanceof Error ? err.message : 'Failed to update main node');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMainNode = async () => {
    const confirmed = await confirm({
      title: 'Remove Main Node',
      message: 'Are you sure you want to remove the Main Node? This will disconnect your field from the network.',
      confirmText: 'Remove',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await removeOfficeNode();
      setSuccess('Main Node removed');
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove main node');
    }
  };

  // Find the linked field node for display
  const linkedMainNode = nodes.find(n => n.id === officeNode?.fieldNodeId);

  const assignedNodes = nodes.filter(n => n.mapId === selectedMap?.id);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      {/* Main Node Section - only for owners */}
      {isOwner && (
        <MainNodeSection>
          <MainNodeHeader>
            <MainNodeTitle>
              <MainNodeIcon>📡</MainNodeIcon>
              Main Node (Office Gateway)
            </MainNodeTitle>
            {!officeNode && (
              <ActionButton onClick={handleSetMainNodeClick}>Set Main Node</ActionButton>
            )}
          </MainNodeHeader>

          {officeNode ? (
            <MainNodeCard>
              <MainNodeInfo>
                <MainNodeName>{officeNode.name}</MainNodeName>
                {officeNode.description && (
                  <MainNodeDescription>{officeNode.description}</MainNodeDescription>
                )}
                {linkedMainNode && (
                  <MainNodeMeta>
                    {linkedMainNode.nodeCode} | OfficeNode
                  </MainNodeMeta>
                )}
              </MainNodeInfo>
              <MainNodeActions>
                <SecondaryButton onClick={handleEditMainNodeClick}>Edit</SecondaryButton>
                <DangerButton onClick={handleRemoveMainNode}>Remove</DangerButton>
              </MainNodeActions>
            </MainNodeCard>
          ) : (
            <MainNodeEmpty>
              No Main Node assigned. The Main Node (InternetNode) connects your field to the internet and is required for real-time game data.
            </MainNodeEmpty>
          )}
        </MainNodeSection>
      )}

      <Header>
        <TitleRow>
          <Title>Field Maps</Title>
          {isOwner && <FieldInfoButton title="Maps & Nodes Help" sections={useMainRefereeApi ? mainRefereeMapsInfo : fieldMapsInfo} />}
        </TitleRow>
        {isOwner && <ActionButton onClick={handleCreateClick}>+ Create Map</ActionButton>}
      </Header>

      {error && !modalOpen && !nodesModalOpen && !mainNodeModalOpen && <ErrorMessage>{error}</ErrorMessage>}
      {success && !modalOpen && !nodesModalOpen && !mainNodeModalOpen && <SuccessMessage>{success}</SuccessMessage>}

      {maps.length > 0 ? (
        <MapGrid>
          {maps.map((map) => (
            <MapCard
              key={map.id}
              onClick={() => navigate(`/fields/${fieldId}/maps/${map.id}?from=${useMainRefereeApi ? 'main-referee' : 'field'}`)}
            >
              <MapImage $imageUrl={(map.schemaImageUrl && map.schemaImageUrl.trim() !== '' ? map.schemaImageUrl : map.exportedImageUrl) || (map.images && map.images[0])}>
                {!(map.schemaImageUrl && map.schemaImageUrl.trim() !== '') && !map.exportedImageUrl && (!map.images || map.images.length === 0) && 'No Image'}
              </MapImage>
              <MapContent>
                <MapName>{map.name}</MapName>
                {map.description && (
                  <MapDescription>
                    {map.description.length > 100
                      ? `${map.description.substring(0, 100)}...`
                      : map.description}
                  </MapDescription>
                )}
                <MapMeta>
                  {isOwner && <span>{nodes.filter(n => n.mapId === map.id).length} nodes</span>}
                  {map.images && map.images.length > 0 && <span>{map.images.length} photos</span>}
                  {map.youtubeVideos && map.youtubeVideos.length > 0 && <span>{map.youtubeVideos.length} videos</span>}
                </MapMeta>
                {isOwner && (
                  <MapActions>
                    <SecondaryButton onClick={(e) => { e.stopPropagation(); handleEditClick(map); }}>Edit</SecondaryButton>
                    <SecondaryButton onClick={(e) => { e.stopPropagation(); handleNodesClick(map); }}>Nodes</SecondaryButton>
                    <SecondaryButton onClick={(e) => { e.stopPropagation(); navigate(`/map-editor/${map.id}`); }}>
                      Schema Editor
                    </SecondaryButton>
                    <DangerButton onClick={(e) => { e.stopPropagation(); handleDeleteClick(map); }}>Delete</DangerButton>
                  </MapActions>
                )}
              </MapContent>
            </MapCard>
          ))}
        </MapGrid>
      ) : (
        <EmptyState>
          No maps created yet. Create your first map to start managing your field layout.
        </EmptyState>
      )}

      {/* Create/Edit Modal */}
      {modalOpen && (
        <ModalOverlay onClick={() => setModalOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editingMap ? 'Edit Map' : 'Create Map'}</ModalTitle>
              <CloseButton onClick={() => setModalOpen(false)}>&times;</CloseButton>
            </ModalHeader>
            <ModalBody>
              <form onSubmit={handleSubmit}>
                <FormGroup>
                  <FormLabel>Map Name *</FormLabel>
                  <FormInput
                    value={formState.name}
                    onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                    placeholder="Enter map name"
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel>Description</FormLabel>
                  <FormTextarea
                    value={formState.description}
                    onChange={(e) => setFormState({ ...formState, description: e.target.value })}
                    placeholder="Map description"
                  />
                </FormGroup>

                {/* Schema Image */}
                <FormGroup>
                  <FormLabel>Schema Image (map layout)</FormLabel>
                  <FileInputWrapper>
                    <FileInput
                      type="file"
                      ref={schemaImageInputRef}
                      accept="image/*"
                      onChange={handleSchemaImageChange}
                      id="schema-image"
                    />
                    <FileInputLabel htmlFor="schema-image">
                      Click to upload schema image
                    </FileInputLabel>
                  </FileInputWrapper>
                  {schemaImagePreview && (
                    <ImagePreview>
                      <img src={schemaImagePreview} alt="Schema Preview" />
                    </ImagePreview>
                  )}
                </FormGroup>

                {/* Schema Video URL */}
                <FormGroup>
                  <FormLabel>Schema Video URL (YouTube)</FormLabel>
                  <FormInput
                    value={formState.schemaVideoUrl}
                    onChange={(e) => setFormState({ ...formState, schemaVideoUrl: e.target.value })}
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                </FormGroup>

                {/* Map Photos */}
                <FormGroup>
                  <FormLabel>Map Photos (up to 5)</FormLabel>
                  <FileInputWrapper>
                    <FileInput
                      type="file"
                      ref={imagesInputRef}
                      accept="image/*"
                      multiple
                      onChange={handleImagesChange}
                      id="map-images"
                    />
                    <FileInputLabel htmlFor="map-images">
                      Click to add photos ({5 - existingImages.length + imagesToDelete.length - imageFiles.length} slots available)
                    </FileInputLabel>
                  </FileInputWrapper>

                  {/* Existing images */}
                  {existingImages.filter(url => !imagesToDelete.includes(url)).length > 0 && (
                    <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {existingImages
                        .filter(url => !imagesToDelete.includes(url))
                        .map((url, index) => (
                          <div key={`existing-${index}`} style={{ position: 'relative' }}>
                            <img src={url} alt={`Photo ${index + 1}`} style={{ width: '80px', height: '60px', objectFit: 'cover', borderRadius: '4px' }} />
                            <button
                              type="button"
                              onClick={() => handleRemoveExistingImage(url)}
                              style={{
                                position: 'absolute',
                                top: '-8px',
                                right: '-8px',
                                background: '#ff4444',
                                border: 'none',
                                borderRadius: '50%',
                                width: '20px',
                                height: '20px',
                                color: '#fff',
                                cursor: 'pointer',
                                fontSize: '12px',
                                lineHeight: '1',
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                    </div>
                  )}

                  {/* New image previews */}
                  {imagePreviews.length > 0 && (
                    <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {imagePreviews.map((preview, index) => (
                        <div key={`new-${index}`} style={{ position: 'relative' }}>
                          <img src={preview} alt={`New photo ${index + 1}`} style={{ width: '80px', height: '60px', objectFit: 'cover', borderRadius: '4px' }} />
                          <button
                            type="button"
                            onClick={() => handleRemoveNewImage(index)}
                            style={{
                              position: 'absolute',
                              top: '-8px',
                              right: '-8px',
                              background: '#ff4444',
                              border: 'none',
                              borderRadius: '50%',
                              width: '20px',
                              height: '20px',
                              color: '#fff',
                              cursor: 'pointer',
                              fontSize: '12px',
                              lineHeight: '1',
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </FormGroup>

                {/* YouTube Videos */}
                <FormGroup>
                  <FormLabel>YouTube Videos</FormLabel>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <FormInput
                      value={youtubeInput}
                      onChange={(e) => setYoutubeInput(e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=..."
                      style={{ flex: 1 }}
                    />
                    <SecondaryButton type="button" onClick={handleAddYoutubeVideo}>
                      Add
                    </SecondaryButton>
                  </div>
                  {formState.youtubeVideos.length > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      {formState.youtubeVideos.map((url, index) => (
                        <div
                          key={index}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px 12px',
                            background: 'rgba(0, 20, 40, 0.6)',
                            borderRadius: '6px',
                            marginBottom: '8px',
                          }}
                        >
                          <span style={{ fontSize: '0.85rem', color: '#b0b0b0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '400px' }}>
                            {url}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveYoutubeVideo(index)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: '#ff4444',
                              cursor: 'pointer',
                              fontSize: '1rem',
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </FormGroup>

                {error && <ErrorMessage>{error}</ErrorMessage>}

                <FormActions>
                  <SecondaryButton type="button" onClick={() => setModalOpen(false)}>
                    Cancel
                  </SecondaryButton>
                  <ActionButton type="submit" disabled={saving}>
                    {saving ? 'Saving...' : editingMap ? 'Update' : 'Create'}
                  </ActionButton>
                </FormActions>
              </form>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Nodes Modal */}
      {nodesModalOpen && selectedMap && (
        <ModalOverlay onClick={() => setNodesModalOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Manage Nodes - {selectedMap.name}</ModalTitle>
              <CloseButton onClick={() => setNodesModalOpen(false)}>&times;</CloseButton>
            </ModalHeader>
            <ModalBody>
              <NodesSection>
                <SectionTitle>Assigned Nodes ({assignedNodes.length})</SectionTitle>
                {assignedNodes.length > 0 ? (
                  <NodesList>
                    {assignedNodes.map((node) => (
                      <NodeItem key={node.id}>
                        <NodeInfo>
                          <NodeName>
                            {node.friendlyName}
                            <NodeTypeBadge $type={node.nodeType}>
                              {node.nodeType.replace('Node', '')}
                            </NodeTypeBadge>
                          </NodeName>
                          <NodeMeta>
                            {node.nodeCode}
                            {node.position && ` | ${node.position}`}
                          </NodeMeta>
                        </NodeInfo>
                        <SecondaryButton onClick={() => handleUnassignNode(node.id)}>
                          Remove
                        </SecondaryButton>
                      </NodeItem>
                    ))}
                  </NodesList>
                ) : (
                  <EmptyState style={{ padding: '20px' }}>No nodes assigned to this map</EmptyState>
                )}
              </NodesSection>

              <NodesSection>
                <SectionTitle>Add Node by Node Code</SectionTitle>
                <AddNodeRow>
                  <SaleNumberInput
                    value={nodeCodeInput}
                    onChange={(e) => setNodeCodeInput(e.target.value)}
                    placeholder="Enter node code"
                    onKeyDown={(e) => e.key === 'Enter' && handleSearchNode()}
                  />
                  <ActionButton
                    type="button"
                    onClick={handleSearchNode}
                    disabled={!nodeCodeInput.trim() || searchingNode}
                  >
                    {searchingNode ? 'Searching...' : 'Search'}
                  </ActionButton>
                </AddNodeRow>

                {nodeSearchError && (
                  <ErrorMessage style={{ marginTop: '12px' }}>{nodeSearchError}</ErrorMessage>
                )}

                {foundNode && (
                  <>
                    <FoundNodeCard>
                      <NodeInfo>
                        <NodeName>
                          {foundNode.friendlyName}
                          <NodeTypeBadge $type={foundNode.nodeType}>
                            {foundNode.nodeType.replace('Node', '')}
                          </NodeTypeBadge>
                        </NodeName>
                        <NodeMeta>
                          {foundNode.nodeCode}
                          {foundNode.mapId && ' | Currently assigned to another map'}
                        </NodeMeta>
                      </NodeInfo>
                    </FoundNodeCard>
                    <FormGroup style={{ marginTop: '12px', marginBottom: '0' }}>
                      <FormLabel>Position Description (optional)</FormLabel>
                      <FormTextarea
                        value={positionInput}
                        onChange={(e) => setPositionInput(e.target.value)}
                        placeholder="e.g., North entrance near the big tree, Base A left corner behind the barricade"
                        style={{ minHeight: '80px' }}
                      />
                    </FormGroup>
                    <ActionButton
                      type="button"
                      onClick={handleAssignFoundNode}
                      style={{ marginTop: '12px' }}
                    >
                      {foundNode.mapId ? 'Reassign to this map' : 'Assign to map'}
                    </ActionButton>
                  </>
                )}
              </NodesSection>

              {error && <ErrorMessage>{error}</ErrorMessage>}
              {success && <SuccessMessage>{success}</SuccessMessage>}

              <FormActions>
                <SecondaryButton type="button" onClick={() => setNodesModalOpen(false)}>
                  Close
                </SecondaryButton>
              </FormActions>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Main Node Modal */}
      {mainNodeModalOpen && (
        <ModalOverlay onClick={() => setMainNodeModalOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editingMainNode ? 'Edit Main Node' : 'Set Main Node'}</ModalTitle>
              <CloseButton onClick={() => setMainNodeModalOpen(false)}>&times;</CloseButton>
            </ModalHeader>
            <ModalBody>
              {!editingMainNode && (
                <>
                  <FormGroup>
                    <FormLabel>Search by Node Code</FormLabel>
                    <AddNodeRow>
                      <SaleNumberInput
                        value={mainNodeCode}
                        onChange={(e) => setMainNodeCode(e.target.value)}
                        placeholder="Enter node code"
                        onKeyDown={(e) => e.key === 'Enter' && handleSearchMainNode()}
                      />
                      <ActionButton
                        type="button"
                        onClick={handleSearchMainNode}
                        disabled={!(mainNodeCode || '').trim() || mainNodeSearching}
                      >
                        {mainNodeSearching ? 'Searching...' : 'Search'}
                      </ActionButton>
                    </AddNodeRow>
                  </FormGroup>

                  {foundMainNode && (
                    <FoundNodeCard>
                      <NodeInfo>
                        <NodeName>
                          {foundMainNode.friendlyName}
                          <NodeTypeBadge $type={foundMainNode.nodeType}>
                            {foundMainNode.nodeType.replace('Node', '')}
                          </NodeTypeBadge>
                        </NodeName>
                        <NodeMeta>
                          {foundMainNode.nodeCode}
                        </NodeMeta>
                      </NodeInfo>
                    </FoundNodeCard>
                  )}
                </>
              )}

              {(foundMainNode || editingMainNode) && (
                <>
                  <FormGroup>
                    <FormLabel>Display Name *</FormLabel>
                    <FormInput
                      value={mainNodeName}
                      onChange={(e) => setMainNodeName(e.target.value)}
                      placeholder="e.g., Office Main Node, Reception Gateway"
                    />
                  </FormGroup>

                  <FormGroup>
                    <FormLabel>Description (optional)</FormLabel>
                    <FormTextarea
                      value={mainNodeDescription}
                      onChange={(e) => setMainNodeDescription(e.target.value)}
                      placeholder="e.g., Located in the main office near the WiFi router"
                      style={{ minHeight: '80px' }}
                    />
                  </FormGroup>
                </>
              )}

              {mainNodeError && <ErrorMessage>{mainNodeError}</ErrorMessage>}

              <FormActions>
                <SecondaryButton type="button" onClick={() => setMainNodeModalOpen(false)}>
                  Cancel
                </SecondaryButton>
                {editingMainNode ? (
                  <ActionButton
                    type="button"
                    onClick={handleUpdateMainNode}
                    disabled={!(mainNodeName || '').trim() || saving}
                  >
                    {saving ? 'Saving...' : 'Update'}
                  </ActionButton>
                ) : (
                  <ActionButton
                    type="button"
                    onClick={handleAssignMainNode}
                    disabled={!foundMainNode || !(mainNodeName || '').trim() || saving}
                  >
                    {saving ? 'Saving...' : 'Set as Main Node'}
                  </ActionButton>
                )}
              </FormActions>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      <ConfirmModal {...confirmModalProps} />
    </Container>
  );
};

export default FieldMapsSection;
